import { styled } from '@/src/stitches.config'
import Image from 'next/image'
import { TEgiftInfo } from '@/src/common/queries/FetchEgift'
import { usePGPlusStepAndModalContext } from '@/src/common/contexts/PGPlusModalAndStepContext'

const ReceiptWrapper = styled('div', {
    display: 'flex',
    fd: 'column',
    ai: 'center',
    fontSize: '.85rem',
    width: '400px',
    maxWidth: '100%',
    padding: '20px 30px',
    backgroundColor: '$white',
    boxShadow: 'gray 0px 1px 10px -1px',
    my: 10,
    '@sm': {
        mt: 0,
        mb: 5,
    },
})

const LabelWrapper = styled('div', {
    display: 'flex',
    jc: 'space-between',
    maxWidth: '100%',
    gap: 10,
    textAlign: 'left',
    '& p': {
        my: 0,
        minWidth: 78,
    },
    '.bold-details': {
        fontWeight: 'bold',
        textAlign: 'right',
    },
    variants: {
        width: {
            default: {
                width: '100%',
            },
        },
        marginBottom: {
            default: {},
            '30px': {
                marginBottom: 30,
            },
        },
    },
    defaultVariants: {
        width: 'default',
        marginBottom: 'default',
    },
})

const ImageWrapper = styled('div', {
    mb: 15,
    '& img': { width: 150, maxWidth: '100%', height: 'auto', br: 5 },
})

const BarcodeWrapper = styled('div', {
    '& img': { width: 233, maxWidth: '100%', height: 'auto' },
    my: 25,
})

const TermsWrapper = styled('div', {
    width: '100%',
    marginTop: 60,
})

interface IReceipt {
    data: TEgiftInfo
}

const GiftReceipt = ({ data }: IReceipt) => {
    const {
        date_created,
        amount,
        image,
        terms,
        description,
        instructions,
        brand_name,
        brand_image,
        code,
        pin,
        expiration_date,
        barcode,
    } = data
    const { giftState } = usePGPlusStepAndModalContext()
    const { cardUuid } = giftState
    const amountInDollars = (amount / 100).toFixed(2)
    const imgSrc = brand_image ?? image

    return (
        <ReceiptWrapper data-testid={'gift-receipt'}>
            <ImageWrapper>
                {!!imgSrc && (
                    <Image
                        src={imgSrc}
                        alt={`${brand_name} eGift Image`}
                        height={120}
                        width={200}
                        data-testid={'egift-image'}
                    />
                )}
            </ImageWrapper>
            <LabelWrapper>
                <p>Merchant:</p>
                <p className={'bold-details'} data-testid={'receipt-brand'}>
                    {brand_name}
                </p>
            </LabelWrapper>
            <LabelWrapper>
                <p>Value:</p>
                <p className={'bold-details'} data-testid={'receipt-amount'}>
                    ${amountInDollars}
                </p>
            </LabelWrapper>
            <LabelWrapper marginBottom={'30px'}>
                <p>Date received:</p>
                <p className={'bold-details'} data-testid={'receipt-date'}>
                    {date_created}
                </p>
            </LabelWrapper>
            <LabelWrapper>
                <p>Gift ID:</p>
                <p className={'bold-details'} data-testid={'receipt-gift-id'}>
                    {cardUuid}
                </p>
            </LabelWrapper>
            <BarcodeWrapper className={'fs-mask fs-exclude'}>
                {!!barcode && (
                    /* eslint-disable @next/next/no-img-element */
                    <img
                        src={barcode}
                        height={30}
                        width={233}
                        alt={'barcode'}
                        data-testid={'barcode'}
                    />
                )}
            </BarcodeWrapper>
            {!!code && (
                <LabelWrapper>
                    <p>eGift card #:</p>
                    <p
                        className={'bold-details fs-mask fs-exclude'}
                        data-testid={'receipt-code'}
                    >
                        {code}
                    </p>
                </LabelWrapper>
            )}
            {!!pin && (
                <LabelWrapper>
                    <p>PIN:</p>
                    <p
                        className={'bold-details fs-mask fs-exclude'}
                        data-testid={'receipt-pin'}
                    >
                        {pin}
                    </p>
                </LabelWrapper>
            )}
            {!!expiration_date && (
                <LabelWrapper>
                    <p>Expiration:</p>
                    <p
                        className={'bold-details'}
                        data-testid={'receipt-expiration'}
                    >
                        {expiration_date}
                    </p>
                </LabelWrapper>
            )}
            <TermsWrapper>
                <p data-testid={'receipt-description'}>{description}</p>
                <p data-testid={'receipt-instructions'}>{instructions}</p>
                <p data-testid={'receipt-terms'}>{terms}</p>
            </TermsWrapper>
        </ReceiptWrapper>
    )
}

export default GiftReceipt
